import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Popover,
    CircularProgress,
    Divider
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Notification from "./notification";
import ProfileDropdown from "./ProfileDropDown";
import { useSelector } from "react-redux";
import { getUserAttendance, checkOut, startLunch, endLunch, startBreak, endBreak } from "../../../api/attendanceAPI";
import moment from "moment";
import TopSearch from "./TopSearch";

const Top = ({ toggleDrawer }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.tokens);
    const [anchorEl, setAnchorEl] = useState(null);
    const [todayAtt, setTodayAtt] = useState(null);
    const [openReason, setOpenReason] = useState(false);
    const [reason, setReason] = useState("");
    const [lunchAnchorEl, setLunchAnchorEl] = useState(null);
    const [lunchElapsed, setLunchElapsed] = useState(0);
    const [breakAnchorEl, setBreakAnchorEl] = useState(null);
    const [breakElapsed, setBreakElapsed] = useState(0);
    const [totalBreakElapsed, setTotalBreakElapsed] = useState(0);

    const fetchAttendance = async () => {
        try {
            if (user?._id) {
                const res = await getUserAttendance(user._id);
                if (res.data && res.data.success) {
                    const todayStr = moment().format('YYYY-MM-DD');
                    const log = res.data.log.find(l => l.date === todayStr);
                    setTodayAtt(log);
                }
            }
        } catch (err) { console.error(err); }
    };

    React.useEffect(() => {
        fetchAttendance();
    }, [user, location.pathname]); // Re-fetch on navigation to handle state sync

    React.useEffect(() => {
        let interval = null;
        
        const calcTotals = () => {
            if (todayAtt && todayAtt.lunchStart && !todayAtt.lunchEnd) {
                const start = moment(todayAtt.lunchStart);
                setLunchElapsed(moment().diff(start, 'seconds'));
            } else {
                setLunchElapsed(0);
            }

            if (todayAtt && todayAtt.shortBreaks?.length > 0) {
                let total = 0;
                let currentBreak = 0;
                todayAtt.shortBreaks.forEach(b => {
                    if (b.end) {
                        total += moment(b.end).diff(moment(b.start), 'seconds');
                    } else {
                        currentBreak = moment().diff(moment(b.start), 'seconds');
                        total += currentBreak;
                    }
                });
                setBreakElapsed(currentBreak);
                setTotalBreakElapsed(total);
            } else {
                setBreakElapsed(0);
                setTotalBreakElapsed(0);
            }
        };

        if ((todayAtt && todayAtt.lunchStart && !todayAtt.lunchEnd) || (todayAtt && todayAtt.shortBreaks?.length > 0 && !todayAtt.shortBreaks[todayAtt.shortBreaks.length - 1].end)) {
            interval = setInterval(calcTotals, 1000);
        }
        
        // Initial calc whether interval starts or not
        calcTotals();
        
        return () => {
            if(interval) clearInterval(interval);
        };
    }, [todayAtt]);

    const handleCheckOut = async () => {
        try {
            const now = moment();
            const hours = now.hours();
            const minutes = now.minutes();
            const totalMinutes = hours * 60 + minutes;
            const startLimit = 13 * 60 + 30; // 1:30 PM
            const endLimit = 17 * 60 + 30; // 5:30 PM

            if (totalMinutes >= startLimit && totalMinutes < endLimit && !reason) {
                setOpenReason(true);
                return;
            }

            const todayStr = moment().format('YYYY-MM-DD');
            await checkOut({ user: user._id, date: todayStr, earlyCheckoutReason: reason });
            setOpenReason(false);
            setReason("");
            fetchAttendance();
        } catch (err) { console.error(err); }
    };

    const handleStartLunch = async () => {
        try {
            const todayStr = moment().format('YYYY-MM-DD');
            await startLunch({ user: user._id, date: todayStr });
            fetchAttendance();
        } catch(err) {
            console.error(err);
            alert(err.response?.data?.message || "Error starting lunch");
        }
    };

    const handleEndLunch = async () => {
        try {
            const todayStr = moment().format('YYYY-MM-DD');
            await endLunch({ user: user._id, date: todayStr });
            setLunchAnchorEl(null);
            fetchAttendance();
        } catch(err) {
            console.error(err);
            alert(err.response?.data?.message || "Error ending lunch");
        }
    };

    const handleStartBreak = async () => {
        try {
            const todayStr = moment().format('YYYY-MM-DD');
            await startBreak({ user: user._id, date: todayStr });
            fetchAttendance();
        } catch(err) {
            console.error(err);
            alert(err.response?.data?.message || "Error starting break");
        }
    };

    const handleEndBreak = async () => {
        try {
            const todayStr = moment().format('YYYY-MM-DD');
            await endBreak({ user: user._id, date: todayStr });
            setBreakAnchorEl(null);
            fetchAttendance();
        } catch(err) {
            console.error(err);
            alert(err.response?.data?.message || "Error ending break");
        }
    };

    const formatTime = (totalSeconds) => {
        const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
        const s = (totalSeconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    const handleLunchClick = (event) => {
        setLunchAnchorEl(event.currentTarget);
    };

    const handleLunchClose = () => {
        setLunchAnchorEl(null);
    };

    const handleBreakClick = (event) => {
        setBreakAnchorEl(event.currentTarget);
    };

    const handleBreakClose = () => {
        setBreakAnchorEl(null);
    };

    const handleProfileClick = (event) => {
        setAnchorEl(event.currentTarget);
    }

    const handleProfileClose = () => {
        setAnchorEl(null);
    }

    const isBreakActive = todayAtt?.shortBreaks?.length > 0 && !todayAtt.shortBreaks[todayAtt.shortBreaks.length - 1].end;

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    boxShadow: '0px 4px 20px rgba(25, 118, 210, 0.3)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 0,
                }}
            >
                <Toolbar sx={{ minHeight: '64px !important' }}>
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        sx={{
                            mr: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'rotate(90deg)',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                        }}
                        onClick={toggleDrawer}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            fontWeight: 600,
                            letterSpacing: '0.5px',
                            textShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        Easo Suite
                    </Typography>

                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                        <TopSearch />
                    </Box>

                    {location.pathname !== "/createItinerary" && (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => navigate("/createItinerary")}
                            sx={{
                                mr: 2,
                                background: 'linear-gradient(135deg, #e9e067ff 0%, #059669 100%)', // Premium Emerald
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                boxShadow: '0px 4px 12px rgba(16, 185, 129, 0.35)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0px 6px 20px rgba(16, 185, 129, 0.45)',
                                }
                            }}
                        >
                            Create New Inquiry
                        </Button>
                    )}

                    {todayAtt && !todayAtt.checkOut && (
                        <Button
                            variant="contained"
                            size="small"
                            color="error"
                            onClick={handleCheckOut}
                            sx={{
                                mr: 2,
                                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                boxShadow: '0px 4px 12px rgba(244, 67, 54, 0.35)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0px 6px 20px rgba(244, 67, 54, 0.45)',
                                }
                            }}
                        >
                            Check Out
                        </Button>
                    )}

                    {todayAtt && !todayAtt.checkOut && !todayAtt.lunchStart && !isBreakActive && (
                        <Button
                            variant="contained"
                            size="small"
                            color="warning"
                            onClick={handleStartLunch}
                            sx={{
                                mr: 2,
                                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                boxShadow: '0px 4px 12px rgba(255, 152, 0, 0.35)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0px 6px 20px rgba(255, 152, 0, 0.45)',
                                }
                            }}
                        >
                            Start Lunch
                        </Button>
                    )}

                    {todayAtt && !todayAtt.checkOut && (!todayAtt.lunchStart || todayAtt.lunchEnd) && !isBreakActive && (
                        <Box sx={{ position: 'relative', display: 'inline-block', mr: 2 }}>
                            {totalBreakElapsed > 0 && (
                                <Box sx={{ 
                                    position: 'absolute', top: -10, right: -10, bgcolor: '#ba68c8', color: '#fff',
                                    borderRadius: '10px', px: 1, py: 0.2, fontSize: '0.65rem', fontWeight: 'bold', zIndex: 1,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}>
                                    {formatTime(totalBreakElapsed)}
                                </Box>
                            )}
                            <Button
                                variant="contained"
                                size="small"
                                color="info"
                                onClick={handleStartBreak}
                                sx={{
                                    background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0px 4px 12px rgba(2, 136, 209, 0.35)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #01579b 0%, #004d40 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0px 6px 20px rgba(2, 136, 209, 0.45)',
                                    }
                                }}
                            >
                                Start Break
                            </Button>
                        </Box>
                    )}

                    {todayAtt && !todayAtt.checkOut && isBreakActive && (
                        <>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleBreakClick}
                                startIcon={<AccessTimeIcon />}
                                sx={{
                                    mr: 2,
                                    background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0px 4px 12px rgba(156, 39, 176, 0.35)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0px 6px 20px rgba(156, 39, 176, 0.45)',
                                    }
                                }}
                            >
                                {formatTime(breakElapsed)}
                            </Button>
                            
                            <Popover
                                open={Boolean(breakAnchorEl)}
                                anchorEl={breakAnchorEl}
                                onClose={handleBreakClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                PaperProps={{
                                    sx: {
                                        mt: 1.5,
                                        p: 3,
                                        width: 280,
                                        borderRadius: 4,
                                        boxShadow: '0px 10px 30px rgba(0,0,0,0.15)',
                                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        border: '1px solid rgba(0,0,0,0.05)'
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                                    <CircularProgress 
                                        variant="determinate" 
                                        value={Math.min((breakElapsed / 900) * 100, 100)} 
                                        size={120} 
                                        thickness={2}
                                        sx={{ color: '#9c27b0' }} 
                                    />
                                    <Box sx={{
                                        top: 0, left: 0, bottom: 0, right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <LocalCafeIcon sx={{ fontSize: 40, color: '#ba68c8', opacity: 0.8 }} />
                                    </Box>
                                </Box>
                                
                                <Typography variant="h4" fontWeight="800" color="text.primary">
                                    {formatTime(breakElapsed)}
                                </Typography>
                                
                                {totalBreakElapsed > breakElapsed && (
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, fontWeight: 'bold', bgcolor: '#f3e5f5', px: 2, py: 0.5, borderRadius: 10 }}>
                                        Total Today: {formatTime(totalBreakElapsed)}
                                    </Typography>
                                )}
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center', mb: 3 }}>
                                    Short breaks are great for staying productive!
                                </Typography>

                                <Divider sx={{ width: '100%', mb: 2 }} />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleEndBreak}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                        boxShadow: '0px 4px 12px rgba(156, 39, 176, 0.3)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)',
                                        }
                                    }}
                                >
                                    Finish Break
                                </Button>
                            </Popover>
                        </>
                    )}

                    {todayAtt && !todayAtt.checkOut && todayAtt.lunchStart && !todayAtt.lunchEnd && (
                        <>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleLunchClick}
                                startIcon={<AccessTimeIcon />}
                                sx={{
                                    mr: 2,
                                    background: lunchElapsed > 3600 ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' : 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0px 4px 12px rgba(33, 150, 243, 0.35)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: lunchElapsed > 3600 ? 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)' : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0px 6px 20px rgba(33, 150, 243, 0.45)',
                                    }
                                }}
                            >
                                {formatTime(lunchElapsed)}
                            </Button>
                            
                            <Popover
                                open={Boolean(lunchAnchorEl)}
                                anchorEl={lunchAnchorEl}
                                onClose={handleLunchClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                PaperProps={{
                                    sx: {
                                        mt: 1.5,
                                        p: 3,
                                        width: 280,
                                        borderRadius: 4,
                                        boxShadow: '0px 10px 30px rgba(0,0,0,0.15)',
                                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        border: '1px solid rgba(0,0,0,0.05)'
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                                    <CircularProgress 
                                        variant="determinate" 
                                        value={Math.min((lunchElapsed / 3600) * 100, 100)} 
                                        size={120} 
                                        thickness={2}
                                        sx={{ color: lunchElapsed > 3600 ? '#f44336' : '#2196f3' }} 
                                    />
                                    <Box sx={{
                                        top: 0, left: 0, bottom: 0, right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <RestaurantIcon sx={{ fontSize: 40, color: lunchElapsed > 3600 ? '#f44336' : '#90caf9', opacity: 0.8 }} />
                                    </Box>
                                </Box>
                                
                                <Typography variant="h4" fontWeight="800" color={lunchElapsed > 3600 ? 'error.main' : 'text.primary'}>
                                    {formatTime(lunchElapsed)}
                                </Typography>
                                
                                {lunchElapsed > 3600 && (
                                    <Typography variant="body2" color="error.main" fontWeight="bold" sx={{ mt: 1, mb: 1, px: 2, py: 0.5, bgcolor: '#ffebee', borderRadius: 10 }}>
                                        Overtime: {formatTime(lunchElapsed - 3600)}
                                    </Typography>
                                )}
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center', mb: 3 }}>
                                    Take your time to recharge, but remember you have 1 hour for lunch.
                                </Typography>

                                <Divider sx={{ width: '100%', mb: 2 }} />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="success"
                                    onClick={handleEndLunch}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                        boxShadow: '0px 4px 12px rgba(76, 175, 80, 0.3)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                                        }
                                    }}
                                >
                                    Finish Lunch
                                </Button>
                            </Popover>
                        </>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Notification />
                        <IconButton
                            color="inherit"
                            onClick={handleProfileClick}
                            sx={{
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    transform: 'scale(1.1)',
                                }
                            }}
                        >
                            <AccountCircleIcon sx={{ fontSize: 32 }} />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            <ProfileDropdown
                anchorEl={anchorEl}
                handleClose={handleProfileClose}
            />

            <Dialog open={openReason} onClose={() => setOpenReason(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Early Check Out Reason</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        You are checking out before 5:30 PM. Please provide a reason for the Admin's review.
                    </Typography>
                    <TextField
                        autoFocus
                        label="Reason for early checkout"
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenReason(false)}>Cancel</Button>
                    <Button 
                        onClick={handleCheckOut} 
                        color="error" 
                        variant="contained" 
                        disabled={!reason.trim()}
                    >
                        Confirm Check Out
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default Top;